import { Delete } from 'lucide-react';
import { useEffect, useState } from 'react';
import { PIN_LENGTH } from '@doorly/shared';
import { cn } from '../../lib/cn';

interface PinPadProps {
  /** Appelé dès que `PIN_LENGTH` chiffres sont saisis. Le pavé se vide ensuite. */
  onComplete: (pin: string) => void | Promise<void>;
  disabled?: boolean;
  /** Secoue les points et vide la saisie — à passer à `true` après un refus. */
  shakeKey?: number;
  label?: string;
  /** Id d'un message (erreur) qui décrit le pavé — relié par `aria-describedby`. */
  describedBy?: string;
}

/**
 * Pavé numérique tactile — exigence N5 : utilisable avec des gants, en
 * boutique, sur tablette. Touches de 72 px minimum, espacées, retour visuel
 * immédiat. Les chiffres tapés au clavier physique sont acceptés aussi (test
 * sur poste de travail), mais jamais affichés : seuls des points.
 */
export function PinPad({
  onComplete,
  disabled = false,
  shakeKey = 0,
  label,
  describedBy,
}: PinPadProps) {
  const [digits, setDigits] = useState('');
  const [shaking, setShaking] = useState(false);

  useEffect(() => {
    if (shakeKey === 0) return;
    setDigits('');
    setShaking(true);
    const t = setTimeout(() => setShaking(false), 450);
    return () => clearTimeout(t);
  }, [shakeKey]);

  useEffect(() => {
    if (digits.length !== PIN_LENGTH) return;
    const pin = digits;
    // Laisse le 4e point s'afficher avant la vérification.
    const t = setTimeout(() => {
      void onComplete(pin);
      setDigits('');
    }, 80);
    return () => clearTimeout(t);
  }, [digits, onComplete]);

  useEffect(() => {
    if (disabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (/^\d$/.test(e.key)) setDigits((d) => (d.length < PIN_LENGTH ? d + e.key : d));
      else if (e.key === 'Backspace') setDigits((d) => d.slice(0, -1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [disabled]);

  const press = (k: string) => {
    if (disabled) return;
    if (k === 'del') setDigits((d) => d.slice(0, -1));
    else setDigits((d) => (d.length < PIN_LENGTH ? d + k : d));
  };

  return (
    <div
      className="flex flex-col items-center gap-6 select-none"
      role="group"
      aria-label={label ?? 'Pavé du code PIN'}
      aria-describedby={describedBy}
    >
      {label && <p className="text-body text-ink-mute text-center">{label}</p>}
      <div
        className={cn('flex gap-4', shaking && 'animate-[pinshake_0.4s_ease-in-out]')}
        aria-label={`${digits.length} chiffre${digits.length > 1 ? 's' : ''} saisi${digits.length > 1 ? 's' : ''} sur ${PIN_LENGTH}`}
        role="status"
      >
        {Array.from({ length: PIN_LENGTH }).map((_, i) => (
          <span
            key={i}
            className={cn(
              'h-4 w-4 rounded-full border-2 transition-colors',
              i < digits.length ? 'bg-brand border-brand' : 'border-ink-mute/50',
              shaking && 'border-danger',
            )}
          />
        ))}
      </div>
      <style>{`@keyframes pinshake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-10px)}40%,80%{transform:translateX(10px)}}`}</style>
      <div className="grid grid-cols-3 gap-3">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'del'].map((k, i) =>
          k === '' ? (
            <span key={i} />
          ) : (
            <button
              key={k}
              type="button"
              disabled={disabled}
              onClick={() => press(k)}
              aria-label={k === 'del' ? 'Effacer' : k}
              className={cn(
                'h-[72px] w-[72px] rounded-full text-kpi font-bold text-ink grid place-items-center',
                'bg-surface-alt border border-hair active:bg-brand-soft active:border-brand transition-colors',
                'disabled:opacity-50',
              )}
            >
              {k === 'del' ? <Delete className="h-7 w-7 text-ink-soft" /> : k}
            </button>
          ),
        )}
      </div>
    </div>
  );
}
